// Nine Lives '84 - browser entry point.
//
// Title screen -> game -> game over -> title. The simulation ticks at
// 60 Hz from keyboard, gamepad and touch input; the three.js world is the
// default view and retro mode (R, or ?view=classic) shows a CGA-style view.

import { Audio } from './audio/audio';
import { PROWL, THEME, WALTZ } from './audio/tune';
import { HighScores } from './hud/highscores';
import { Hud } from './hud/hud';
import { TitleScreen } from './hud/title';
import { GamepadInput } from './input/gamepad';
import { Keyboard } from './input/keyboard';
import { isTouchDevice, TouchInput } from './input/touch';
import { ClassicView } from './render/classic';
import { WorldRenderer } from './render/world';
import { defaultsForDevice, loadSettings, saveSettings, type Settings } from './settings';
import {
  Difficulty,
  DIFFICULTY_NAMES,
  FPS,
  Game,
  type GameConfig,
  K_PAUSE,
  K_SOUND,
  ROOM_KINDS,
  type RoomKind,
} from './sim';

window.addEventListener('error', (e) => {
  console.error('[alleycat] uncaught:', e.message, e.error?.stack);
});

const params = new URLSearchParams(location.search);

const ROOM_TITLES: Record<RoomKind, string> = {
  cheese: 'THE CHEESE: catch the mice',
  bookshelf: 'THE BOOKSHELF: knock the ferns down',
  birdcage: 'THE BIRDCAGE: push it off the table',
  dogs: 'SLEEPING DOGS: hold ↓ at a bowl to drink. Back off when one growls.',
  fishbowl: 'THE FISHBOWL: jump in and eat the fish',
  felicia: 'CLEMENTINE! Climb the hearts. Mind her brothers.',
};

const store = typeof localStorage !== 'undefined' ? localStorage : null;
const touchDevice = isTouchDevice() || params.has('touch');
let settings: Settings = loadSettings(
  store,
  defaultsForDevice(touchDevice, matchMedia('(prefers-reduced-motion: reduce)').matches),
);
if (params.get('view') === 'classic') settings = { ...settings, retro: true };
if (params.has('low')) settings = { ...settings, lowDetail: true };

function config(difficulty: Difficulty): Partial<GameConfig> {
  const seed = Number(params.get('seed'));
  return {
    difficulty,
    seed: seed || (Date.now() & 0xffff) || 7491,
    transitions: !settings.reduceMotion,
  };
}

let game = new Game(config(Difficulty.HOUSE_CAT));
/** For the console and tests: the live game, the audio, and whether it is paused. */
const expose = (): void => {
  (window as unknown as { alleycat: unknown }).alleycat = {
    game,
    audio,
    get paused() {
      return paused;
    },
  };
};

const app = document.getElementById('app')!;
const keyboard = new Keyboard();
const gamepad = new GamepadInput();
const touch = touchDevice ? new TouchInput(app) : null;
const audio = new Audio();
const highs = new HighScores(store);
expose();

if ('serviceWorker' in navigator && !import.meta.env.DEV) {
  // When a newer worker takes over an already-controlled page, reload once
  // so the player gets the new build instead of the cached one.
  const controlled = !!navigator.serviceWorker.controller;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (controlled) location.reload();
  });
  navigator.serviceWorker.register('./sw.js').catch(() => {});
}

interface View {
  bind(game: Game): void;
  commit(): void;
  render(alpha: number, dt: number): void;
  renderAttract(dt: number): void;
  setVisible(v: boolean): void;
}

const world = new WorldRenderer(app, game);
const classic = new ClassicView(app);
const classicView: View = {
  bind: () => {},
  commit: () => {},
  render: () => classic.render(game),
  renderAttract: () => classic.render(game),
  setVisible: (on) => classic.setVisible(on),
};

// Both views exist; retro mode shows the classic one.
let retro = settings.retro;
let view: View = retro ? classicView : world;
const hud = new Hud(app);
hud.touch = !!touch;
const title = new TitleScreen(app, highs);

function setRetro(on: boolean): void {
  retro = on;
  view = on ? classicView : world;
  world.setVisible(!on);
  classicView.setVisible(on);
  hud.setRetro(on);
}

let playing = false;
let paused = false;

function applySettings(s: Settings): void {
  settings = s;
  setRetro(s.retro);
  world.reduceMotion = s.reduceMotion;
  world.setLowDetail(s.lowDetail);
  audio.setVolume(s.volume);
  audio.setMusic(s.music);
  hud.setLegend(s.legend);
  chooseMusic();
  title.settings = s;
  saveSettings(store, s);
}
applySettings(settings);
title.onSettings = applySettings;
hud.onLegend = (shown) => {
  settings = { ...settings, legend: shown };
  saveSettings(store, settings);
};

function setPaused(on: boolean): void {
  if (!playing) return;
  paused = on;
  hud.paused = on;
  accumulator = 0;
}

// Pause when the tab is hidden; nobody wants to come back to a dead cat.
document.addEventListener('visibilitychange', () => {
  if (document.hidden && playing) setPaused(true);
});

if (touch) {
  // A fullscreen button beside the pause button on phones.
  const fs = document.createElement('div');
  fs.className = 'touch-btn touch-fs';
  fs.textContent = '\u26f6';
  fs.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    if (document.fullscreenElement) void document.exitFullscreen();
    else void app.requestFullscreen?.();
  });
  app.appendChild(fs);
}

function startGame(difficulty: Difficulty): void {
  audio.unlock();
  audio.stopTune();
  game = new Game(config(difficulty));
  const forced = params.get('room') as RoomKind | null;
  if (forced && ROOM_KINDS.includes(forced)) game.nextRoom = forced;
  if (forced === 'felicia') game.alley.showFelicia();
  expose();
  world.bind(game);
  game.takeEvents();
  title.hide();
  hud.setVisible(true);
  touch?.setVisible(true);
  playing = true;
  paused = false;
  hud.paused = false;
  accumulator = 0;
  chooseMusic();
}

/** Title theme on the menus, the prowl in the alley and rooms, the waltz for Clementine. */
function chooseMusic(): void {
  if (!audio.ready || !settings.music) return;
  if (!playing) audio.playTune(THEME);
  else if (game.room?.kind === 'felicia') audio.playTune(WALTZ, 0.08);
  else audio.playTune(PROWL, 0.07);
}

function endGame(): void {
  playing = false;
  paused = false;
  hud.paused = false;
  const rank = highs.add({
    score: game.score,
    level: game.level,
    mode: DIFFICULTY_NAMES[game.config.difficulty],
    date: new Date().toISOString().slice(0, 10),
    medals: 0,
  });
  hud.setVisible(false);
  touch?.setVisible(false);
  title.showGameOver(game.score, game.level, game.config.difficulty, rank);
}

title.onStart = ({ difficulty }) => startGame(difficulty);
title.note = import.meta.env.DEV
  ? 'Development build'
  : 'Clear a room and Clementine appears at a window. Reach her for an extra life.';
title.showTitle();
hud.setVisible(false);
touch?.setVisible(false);

// Browsers only allow audio after a gesture: unlock on the first one and
// start the title tune if we are still on the title.
const unlockAudio = (): void => {
  audio.unlock();
  chooseMusic();
};
app.addEventListener('pointerdown', unlockAudio);
window.addEventListener('keydown', unlockAudio);

// F1 toggles the debug readout; R toggles retro mode; Esc quits to the title.
const debug = document.getElementById('debug')!;
let showDebug = params.has('debug');
window.addEventListener('keydown', (e) => {
  if (e.code === 'F1') {
    showDebug = !showDebug;
    e.preventDefault();
  } else if (e.code === 'KeyR' && !e.repeat) {
    applySettings({ ...settings, retro: !retro });
    hud.flashMessage(retro ? 'RETRO MODE' : '3D MODE');
  } else if (e.code === 'Escape' && playing) {
    // First Esc pauses; a second one quits to the title. Quitting has to
    // unpause first, since the paused loop never looks at the game-over flag.
    if (paused) {
      setPaused(false);
      game.end();
    } else {
      setPaused(true);
    }
  }
});

const TICK_MS = 1000 / FPS;
let accumulator = 0;
let last = performance.now();
let fps = 0;
let lastErrorAt = 0;

function frame(now: number): void {
  requestAnimationFrame(frame);
  try {
    step(now);
  } catch (err) {
    // Keep the loop alive; report at most once a second.
    if (now - lastErrorAt > 1000) {
      lastErrorAt = now;
      console.error('[alleycat] frame error:', err);
    }
  }
}

function step(now: number): void {
  const dt = Math.min(now - last, 250);
  last = now;

  if (playing && paused) {
    // Still poll so the pause key (or button) can resume.
    const keys = keyboard.poll() | gamepad.poll() | (touch?.poll() ?? 0);
    if (keys & K_PAUSE) setPaused(false);
    view.render(0, dt / 1000);
    hud.update(game);
  } else if (playing) {
    accumulator += dt;
    while (accumulator >= TICK_MS) {
      const keys = keyboard.poll() | gamepad.poll() | (touch?.poll() ?? 0);
      if (keys & K_SOUND) {
        audio.unlock();
        hud.flashMessage(audio.toggle() ? 'SOUND ON' : 'SOUND OFF');
      }
      if (keys & K_PAUSE) {
        setPaused(true);
        break;
      }
      game.tick(keys);
      world.commit(); // keeps interpolation continuous across a retro switch
      accumulator -= TICK_MS;
    }
    for (const ev of game.takeEvents()) {
      audio.onEvent(ev);
      world.onEvent(ev);
      if (ev.kind === 'roomStart' || ev.kind === 'roomDone' || ev.kind === 'fallOut' || ev.kind === 'kiss') {
        chooseMusic();
      }
      if (ev.kind === 'roomDone') hud.flashMessage(`ROOM CLEARED  +${ev.bonus}`, 2500);
      else if (ev.kind === 'caught') hud.flashMessage('THE DOG GOT YOU', 1200);
      else if (ev.kind === 'roomStart') hud.flashMessage(ROOM_TITLES[ev.room], ev.room === 'dogs' ? 3200 : 1800);
      else if (ev.kind === 'shooed') hud.flashMessage('SWEPT OUT!', 1500);
      else if (ev.kind === 'mouseCaught') hud.flashMessage(`MOUSE ${ev.have} OF ${ev.need}`, 900);
      else if (ev.kind === 'fernDown') hud.flashMessage(`FERN ${ev.have} OF ${ev.need}`, 900);
      else if (ev.kind === 'nudge') hud.flashMessage(`NUDGE ${ev.have} OF ${ev.need}`, 700);
      else if (ev.kind === 'cageBroke') hud.flashMessage('CATCH THE BIRD!', 1500);
      else if (ev.kind === 'growl') hud.flashMessage('GRRR… back off', 900);
      else if (ev.kind === 'dogWoke') hud.flashMessage('THE DOG IS AWAKE!', 1200);
      else if (ev.kind === 'bowlEmpty') hud.flashMessage(`BOWL ${ev.have} OF ${ev.need}`, 900);
      else if (ev.kind === 'refill') hud.flashMessage('REFILL!', 800);
      else if (ev.kind === 'fishEaten') hud.flashMessage(`FISH ${ev.have} OF ${ev.need}`, 700);
      else if (ev.kind === 'shock') hud.flashMessage('ZAP!', 700);
      else if (ev.kind === 'drowned') hud.flashMessage('GLUB… DROWNED', 1200);
      else if (ev.kind === 'felicia') hud.flashMessage('CLEMENTINE IS AT A WINDOW ♥', 2500);
      else if (ev.kind === 'gift') hud.flashMessage(`GIFT ${ev.held} OF 3`, 900);
      else if (ev.kind === 'bribe') hud.flashMessage('BRIBED!', 900);
      else if (ev.kind === 'fallOut') hud.flashMessage('FELL OUT… clear another room', 2000);
      else if (ev.kind === 'kiss')
        hud.flashMessage(`KISS! ♥  +${ev.bonus}  EXTRA LIFE  LEVEL ${ev.level}`, 3500);
    }
    view.render(accumulator / TICK_MS, dt / 1000);
    hud.update(game);
    if (game.isOver) {
      endGame();
      chooseMusic();
    }
  } else if (!playing) {
    view.renderAttract(dt / 1000);
  }

  fps = fps * 0.95 + (1000 / Math.max(dt, 1)) * 0.05;
  debug.hidden = !showDebug;
  if (showDebug) {
    const c = game.cat;
    debug.textContent = [
      `tick ${game.count}  ${fps.toFixed(0)} fps  ${retro ? 'retro' : '3D'}  pad ${gamepad.connected}`,
      `x ${c.x.toFixed(1)}  y ${c.y.toFixed(1)}  vx ${c.vx.toFixed(0)}  vy ${c.vy.toFixed(0)}`,
      `ground ${c.onGround} ${c.ground?.kind ?? '-'}  coyote ${c.coyote}  buffer ${c.jumpBuffer}  leap ${c.leaping}`,
      `state ${c.state}  invuln ${c.invuln}  scene ${game.scene}${game.room ? ` ${game.room.kind} ${game.room.have}/${game.room.need} broom ${game.room.broom.target}` : ''}  open ${game.alley.openCount}`,
      `dog x ${game.alley.dog.x.toFixed(0)} away ${game.alley.dog.away}  mice ${game.alley.mice.length}  junk ${game.alley.junk.length}`,
      `lives ${game.lives}  level ${game.level}  score ${game.score}`,
    ].join('\n');
  }
}
requestAnimationFrame(frame);
